// WHAT: Main chat interface component for Ollama LLM integration with message history and model selection
// WHY:  Provides complete chat UI for interacting with local Ollama models with health monitoring and message management
// DOMAIN: LLM → Ollama → Chat Interface
// DEPENDENCIES: useOllamaChat hook, useModelHealth hook, ModelSelector component, MessageBubble component
// RISKS:
//   - NOTE: Auto-scroll behavior may cause UX issues if user is reading previous messages
//   - NOTE: Health status check is manual - may not reflect real-time connection state
//   - WARN: Message retry functionality may cause duplicate messages if network is unstable
//   - NOTE: Input validation is minimal - may allow empty or malformed messages

import type { ChatWindowProperties } from "../types";

import { Send, RotateCcw, Trash2, Bot, AlertCircle } from "lucide-react";
import { type FC, useState, useRef, useEffect } from "react";

import { Button, Input, Text, Container, Stack } from "@/components/ui";
import { cn } from "@/shared/utils";

import { useModelHealth } from "../hooks/useModelHealth";
import { useOllamaChat } from "../hooks/useOllamaChat";
import { MessageBubble } from "./MessageBubble";
import { ModelSelector } from "./ModelSelector";

/**
 * WHAT:
 *   Main chat interface component for Ollama LLM integration with message history and model selection.
 *
 * WHY:
 *   Provides complete chat UI for interacting with local Ollama models with health monitoring and message management.
 *
 * PROPS:
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Auto-scrolls to bottom when new messages arrive
 *   - Integrates with useOllamaChat hook for message management
 *   - Integrates with useModelHealth hook for model status
 *   - Supports Enter key to send messages (Shift+Enter for new line)
 *   - Displays loading indicator while waiting for responses
 *   - Shows error messages if chat operations fail
 *   - Provides retry functionality for last message
 *   - Disables input when Ollama is unhealthy or no models available
 *
 * RISKS:
 *   - NOTE: Auto-scroll behavior may cause UX issues if user is reading previous messages
 *   - NOTE: Health status check is manual - may not reflect real-time connection state
 *   - WARN: Message retry functionality may cause duplicate messages if network is unstable
 *   - NOTE: Input validation is minimal - may allow empty or malformed messages
 *   - WARN: No message persistence - chat history is lost on page refresh
 *
 * @param root0
 *
 * @param root0.className
 *
 * @returns JSX element with chat interface
 *
 */
export const ChatWindow: FC<ChatWindowProperties> = ({ className }) => {
  const [input, setInput] = useState("");
  const messagesEndReference = useRef<HTMLDivElement>(null);

  const {
    clearMessages,
    currentModel,
    error,
    isLoading,
    messages,
    retryLastMessage,
    sendMessage,
    setModel,
  } = useOllamaChat();

  const { availableModels, checkHealth, healthStatus, isChecking } = useModelHealth();

  // NOTE: Auto-scroll to bottom when new messages arrive - may interrupt user reading
  // PERF: Uses smooth scrolling which may be slower but provides better UX
  // WARN: This effect runs on every messages array change - may cause performance issues with many messages
  useEffect(() => {
    // NOTE: Optional chaining prevents error if ref is not yet attached
    messagesEndReference.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /**
   * WHAT:
   *   Handles sending user message to Ollama chat API.
   *
   * WHY:
   *   Centralizes message sending logic with input validation and state management.
   *
   * SIDE EFFECTS:
   *   - Clears input field
   *   - Triggers API call via useOllamaChat hook
   *   - Updates messages state in hook
   *
   * RISKS:
   *   - NOTE: No input sanitization - may send malicious content
   *   - NOTE: Trims whitespace - may lose intentional formatting
   */
  const handleSendMessage = async () => {
    // NOTE: Prevent sending empty messages or sending while already processing
    if (!input.trim() || isLoading) {
      return;
    }

    // NOTE: Trim whitespace to prevent accidental empty messages
    const message = input.trim();
    // NOTE: Clear input immediately for better UX (optimistic UI update)
    setInput("");
    // NOTE: Send message with current model - model may change between input and send
    await sendMessage(message, currentModel);
  };

  /**
   * WHAT:
   *   Handles keyboard input for message sending (Enter to send, Shift+Enter for new line).
   *
   * WHY:
   *   Provides intuitive keyboard shortcuts for chat interaction.
   *
   * INPUTS:
   *   - e: React.KeyboardEvent — Keyboard event from input field
   *
   * BEHAVIOR:
   *   - Enter key (without Shift) sends message and prevents default newline
   *   - Shift+Enter allows normal textarea behavior (new line)
   *
   * @param e
   *
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // NOTE: Enter key sends message, Shift+Enter creates new line
    // This is standard chat UI behavior
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // NOTE: Prevent default newline insertion
      void handleSendMessage();
    }
    // NOTE: Shift+Enter is allowed to pass through for normal textarea behavior
  };

  const isDisabled = isLoading || !healthStatus.isHealthy || availableModels.length === 0;

  const containerClassName: string = className
    ? cn("h-full flex flex-col", className)
    : "h-full flex flex-col";

  return (
    <Container className={containerClassName} maxWidth="full">
      <Stack className="h-full" direction="col" gap={4}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Bot className="w-6 h-6 text-blue-400" />
            <Text className="text-xl font-semibold text-white">Ollama Chat</Text>
            <div
              className={cn(
                "w-2 h-2 rounded-full",
                healthStatus.isHealthy ? "bg-green-400" : "bg-red-400",
              )}
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              className="text-slate-300 border-slate-600 hover:bg-slate-700"
              disabled={isChecking}
              onClick={() => {
                void checkHealth();
              }}
              size="sm"
              variant="outline"
            >
              <RotateCcw className={cn("w-4 h-4", isChecking && "animate-spin")} />
            </Button>

            <Button
              className="text-slate-300 border-slate-600 hover:bg-slate-700"
              disabled={messages.length === 0}
              onClick={clearMessages}
              size="sm"
              variant="outline"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Model Selector */}
        <div className="px-4">
          <ModelSelector
            availableModels={availableModels}
            currentModel={currentModel}
            isHealthy={healthStatus.isHealthy}
            onModelChange={setModel}
          />
        </div>

        {/* Error Display */}
        {error ? (
          <div className="mx-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <Text className="text-red-400 text-sm">{error}</Text>
            </div>
          </div>
        ) : null}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Bot className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <Text className="text-slate-400 text-lg">Start a conversation with Ollama</Text>
                <Text className="text-slate-500 text-sm mt-2">
                  Select a model and type your message below
                </Text>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <MessageBubble
                  isUser={message.role === "user"}
                  key={message.id}
                  message={message}
                />
              ))}
              {isLoading ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                  <Text className="text-sm">Ollama is thinking...</Text>
                </div>
              ) : null}
              <div ref={messagesEndReference} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex gap-2">
            <Input
              className="flex-1"
              disabled={isDisabled}
              onChange={(e) => {
                setInput(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                isDisabled ? "Ollama is not available" : "Type your message... (Enter to send)"
              }
              value={input}
            />
            <Button
              className="px-4"
              disabled={isDisabled || !input.trim()}
              onClick={() => {
                void handleSendMessage();
              }}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {messages.length > 0 && (
            <div className="mt-2 flex justify-end">
              <Button
                className="text-slate-400 border-slate-600 hover:bg-slate-700"
                disabled={isLoading}
                onClick={() => {
                  void retryLastMessage();
                }}
                size="sm"
                variant="outline"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Retry
              </Button>
            </div>
          )}
        </div>
      </Stack>
    </Container>
  );
};
